import { FC, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { NotificationManager } from "react-notifications";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import serviceSales from "../../services/service-sales";
import appState from "../../state/AppStateContainer";
import styles from "./app-ted-star-button.module.scss";
import { useTedStar } from "./useTedStar";

type Props = {
  procedureIdentifier: string,
  starred: boolean,
};

/** A favourite is the procedure, not the notice, so the star follows it to its award notice. */
const AppTedStarButton: FC<Props> = ({ procedureIdentifier, starred: savedStarred }) => {
  const [t] = useTranslation("requests");
  const { starred, toggle } = useTedStar(procedureIdentifier, savedStarred);

  const save = async (next: boolean) => {
    if (next) {
      await serviceSales.starTedProcedure(procedureIdentifier);
      // The server's rule: only a reader who never chose is switched on.
      if (appState.getUserNotificationPreferences().weeklyDigestPublic === undefined) {
        appState.setUserNotificationPreference("weeklyDigestPublic", true);
      }
    } else {
      await serviceSales.unstarTedProcedure(procedureIdentifier);
    }
  };

  const onClick = async (event: MouseEvent<HTMLButtonElement>) => {
    // The row toggles its detail panel on click.
    event.stopPropagation();
    try {
      await toggle(save);
    } catch {
      NotificationManager.error(t("published.ted.starError", "Kunde inte spara favoritmarkeringen"));
    }
  };

  const Icon = starred ? StarIcon : StarBorderIcon;

  return (
    <button
      type="button"
      className={`${styles.star} ${starred ? styles.starred : ""}`}
      // A toggle keeps one name and reports its state through aria-pressed.
      aria-label={t("published.ted.star", "Favoritmarkera")}
      aria-pressed={starred}
      title={starred
        ? t("published.ted.unstar", "Ta bort favoritmarkering")
        : t("published.ted.star", "Favoritmarkera")}
      onClick={onClick}
    >
      <Icon fontSize="inherit" />
    </button>
  );
};

export default AppTedStarButton;
